import { useState } from "react";
import { EyeOff } from "lucide-react";
import { useAppDispatch } from "@/app/hooks";
import { endAdminImpersonation } from "@/lib/auth/endAdminImpersonation";
import { showApiError } from "@/lib/utils/notifications";

interface StopImpersonateButtonProps {
  /** Optional test id for E2E (e.g. page-specific: applicant-help-*, applicant-profile-*). */
  dataTestId?: string;
}

/**
 * Standalone "Stop Impersonate" button for use on pages that don't use ApplicationHeader
 * (e.g. Help/About, Profile). Shows only when admin is impersonating.
 */
export function StopImpersonateButton({
  dataTestId,
}: StopImpersonateButtonProps) {
  const dispatch = useAppDispatch();
  const [isAdminImpersonating] = useState(() => {
    const adminToken = localStorage.getItem("admin_token");
    const impersonateFlag = localStorage.getItem("impersonate");
    return !!adminToken || impersonateFlag === "true";
  });
  const [stopping, setStopping] = useState(false);

  const handleStopImpersonate = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      const originalApplicantId = localStorage.getItem("impersonated_applicant_id");
      const redirect = await endAdminImpersonation({
        dispatch,
        applicantFallback: {
          originalApplicantId,
          applicationsListPath: "/application",
        },
      });
      window.location.href = redirect;
    } catch (e) {
      showApiError(e);
      setStopping(false);
    }
  };

  if (!isAdminImpersonating) return null;

  return (
    <button
      type="button"
      data-testid={dataTestId}
      onClick={() => void handleStopImpersonate()}
      disabled={stopping}
      className="flex items-center gap-2 px-3 py-1.5 bg-[rgb(21_151_255)] text-white rounded-md hover:bg-[#0d7acc] transition-colors shadow-sm flex-shrink-0 disabled:opacity-60"
    >
      <EyeOff className="w-4 h-4" />
      <span className="text-sm font-medium">{stopping ? "Stopping…" : "Stop Impersonate"}</span>
    </button>
  );
}
